import { useEffect, useState } from "react";
import { Star, MessageSquareQuote } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { Reveal } from "./Reveal";

// ═══════════════════════════════════════════════════════════════════════════
// URL DA AVALIAÇÃO GOOGLE — substituir pelo link direto de avaliação do
// Perfil de Empresa da AzorUnique no Google (Google Business Profile).
// Onde obter: Perfil de Empresa → "Pedir avaliações" → copiar o link.
// ═══════════════════════════════════════════════════════════════════════════
const GOOGLE_REVIEW_URL = "https://g.page/r/CV6zSoBmaEmuEBM/review";

export const Stars = ({ value, size = 14 }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={size}
        className={i <= value ? "fill-gold text-gold" : "text-mist/40"}
      />
    ))}
  </div>
);

const inputCls =
  "w-full rounded-md border border-white/10 bg-basalt px-3.5 py-2.5 text-sm text-sand outline-none transition-colors focus:border-terracotta";

export const Testimonials = () => {
  const { t } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", project: "", rating: 0, comment: "", consent: false });
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.rating) {
      setError(t.reviews.errRating);
      return;
    }
    if (!form.consent) {
      setError(t.reviews.errConsent);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t.reviews.errGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="avaliacoes" className="border-t border-white/10 bg-surface/40 py-28 sm:py-36" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.35em] text-terracotta">
            {t.reviews.eyebrow}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="max-w-2xl font-display text-2xl font-normal leading-snug tracking-tight text-sand sm:text-3xl lg:text-4xl" data-testid="reviews-title">
            {t.reviews.title}
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-mist sm:text-base">{t.reviews.subtitle}</p>
        </Reveal>

        <div className="mt-16 grid gap-10 lg:grid-cols-5 lg:gap-16">
          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="rounded-xl border border-white/10 bg-basalt p-8">
              {submitted ? (
                <div data-testid="review-success">
                  <MessageSquareQuote size={26} className="text-terracotta" />
                  <p className="mt-5 font-display text-xl leading-snug text-sand" data-testid="review-thanks">
                    {t.reviews.thanks}
                  </p>
                  <p className="mt-6 text-sm leading-relaxed text-mist">{t.reviews.googleLead}</p>
                  <a
                    href={GOOGLE_REVIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="review-google-cta"
                    className="mt-5 inline-block rounded-full bg-terracotta px-7 py-3 text-sm font-medium text-basalt transition-colors hover:bg-sand"
                  >
                    {t.reviews.googleCta}
                  </a>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="review-form">
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sand/60">{t.reviews.formTitle}</p>

                  <label className="mt-6 block">
                    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-mist">
                      {t.reviews.name}
                    </span>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={t.reviews.namePlaceholder}
                      className={inputCls}
                      data-testid="review-name-input"
                    />
                  </label>

                  <label className="mt-5 block">
                    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-mist">
                      {t.reviews.project}
                    </span>
                    <input
                      value={form.project}
                      onChange={(e) => setForm({ ...form, project: e.target.value })}
                      placeholder={t.reviews.projectPlaceholder}
                      className={inputCls}
                      data-testid="review-project-input"
                    />
                  </label>

                  <div className="mt-5">
                    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-mist">
                      {t.reviews.rating}
                    </span>
                    <div className="flex gap-1.5" data-testid="review-rating-input">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setForm({ ...form, rating: i })}
                          onMouseEnter={() => setHover(i)}
                          onMouseLeave={() => setHover(0)}
                          data-testid={`review-star-${i}`}
                          aria-label={`${i} estrelas`}
                          className="transition-transform hover:scale-110"
                        >
                          <Star
                            size={22}
                            className={i <= (hover || form.rating) ? "fill-gold text-gold" : "text-mist/40"}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="mt-5 block">
                    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-mist">
                      {t.reviews.comment}
                    </span>
                    <textarea
                      required
                      rows={4}
                      maxLength={4000}
                      value={form.comment}
                      onChange={(e) => setForm({ ...form, comment: e.target.value })}
                      placeholder={t.reviews.commentPlaceholder}
                      className={inputCls}
                      data-testid="review-comment-input"
                    />
                    <span className="mt-1 block text-right font-mono text-[10px] tracking-widest text-sand/35" data-testid="review-comment-counter">
                      {form.comment.length}/4000
                    </span>
                  </label>

                  <label className="mt-5 flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                      className="mt-0.5 h-4 w-4 accent-[#C86D51]"
                      data-testid="review-consent-checkbox"
                    />
                    <span className="text-xs leading-relaxed text-mist">{t.reviews.consent}</span>
                  </label>

                  {error && (
                    <p className="mt-4 rounded-md border border-terracotta/40 bg-terracotta/10 px-3 py-2 text-xs text-terracotta" data-testid="review-form-error">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="review-submit-button"
                    className="mt-6 w-full rounded-full bg-terracotta py-3 text-sm font-medium text-basalt transition-colors hover:bg-sand disabled:opacity-50"
                  >
                    {submitting ? t.reviews.submitting : t.reviews.submit}
                  </button>
                  <p className="mt-4 text-center font-mono text-[10px] tracking-widest text-sand/35">
                    {t.reviews.moderation}
                  </p>
                </form>
              )}
            </div>
          </Reveal>

          <div className="lg:col-span-3" data-testid="reviews-list">
            {reviews.length === 0 ? (
              <Reveal delay={0.2}>
                <p className="rounded-xl border border-dashed border-white/10 p-10 text-center font-mono text-[11px] uppercase tracking-widest text-mist" data-testid="reviews-empty">
                  {t.reviews.empty}
                </p>
              </Reveal>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2">
                {reviews.map((r, i) => (
                  <Reveal key={r.id} delay={0.08 + i * 0.06} y={24} className="h-full">
                    <figure className="flex h-full flex-col rounded-xl border border-white/10 bg-basalt p-7" data-testid={`review-card-${r.id}`}>
                      <Stars value={r.rating} />
                      <blockquote className="mt-5 font-display text-base italic leading-relaxed text-sand/90">
                        “{r.comment}”
                      </blockquote>
                      <figcaption className="mt-auto pt-6 font-mono text-[11px] uppercase tracking-widest text-mist">
                        <span className="text-terracotta">{r.name}</span>
                        {r.project ? ` · ${r.project}` : ""}
                      </figcaption>
                    </figure>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
